import { useCallback, useEffect, useState } from 'react';
import { fetchDynolangTranslations } from 'src/api/dynolang';
import { useDepartment } from '@hooks/department';
import { FeatureToggles } from '@lib/featureToggles';

// retrieves nested translation data
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown =>
    path
        .split('.')
        .reduce<unknown>(
            (acc, part) =>
                acc instanceof Object
                    ? (acc as Record<string, unknown>)[part]
                    : undefined,
            obj,
        );

const COUNTRY_CODE_TO_LANGUAGE_MAP: Record<string, string> = {
    NO: 'NO',
    DK: 'DK',
    GB: 'EN',
    US: 'EN',
};

const useDynolang = (
    pageCode = 'PAGE_ORG_AND_DEPARTMENTS',
): {
    t: (path: string, fallback: string) => string;
    isLoadingTranslations: boolean;
    error: unknown;
} => {
    const { department } = useDepartment();
    const [language, setLanguage] = useState(
        localStorage.getItem('app_language') ??
            department?.org?.countryRules?.countryCode ??
            'NO',
    );
    const [translations, setTranslations] = useState<Record<
        string,
        unknown
    > | null>(null);

    // TODO: change isLoading into isFirstLoading and add it to loaders
    const [isLoadingTranslations, setIsLoadingTranslations] = useState(true);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
        const handleLanguageChange = (): void => {
            const newLang = localStorage.getItem('app_language') ?? 'NO';
            setLanguage(newLang);
        };

        window.addEventListener('languageChange', handleLanguageChange);

        return (): void => {
            window.removeEventListener('languageChange', handleLanguageChange);
        };
    }, []);

    useEffect(() => {
        const storedLang = localStorage.getItem('app_language');
        const orgCountryCode = department?.org?.countryRules?.countryCode;
        const orgLang = orgCountryCode
            ? (COUNTRY_CODE_TO_LANGUAGE_MAP[orgCountryCode] ?? 'EN')
            : 'EN';

        if (!storedLang && orgLang && language !== orgLang) {
            setLanguage(orgLang);
        }
    }, [department?.org?.countryRules?.countryCode, language]);

    useEffect(() => {
        setIsLoadingTranslations(true);

        const fetchTranslations = async (): Promise<void> => {
            try {
                const response = await fetchDynolangTranslations(
                    pageCode,
                    language,
                );
                setTranslations(response);
            } catch (err) {
                setError(err);
            } finally {
                setIsLoadingTranslations(false);
            }
        };

        fetchTranslations();
    }, [pageCode, language]);

    const t = useCallback(
        (path: string, fallback: string) => {
            if (
                !FeatureToggles.FEATURE_TOGGLE_TRANSLATION_ENABLED.isEnabled() ||
                !translations
            ) {
                return fallback;
            }
            const value = getNestedValue(translations, path);

            if (typeof value !== 'string') {
                console.warn(
                    `Translation for path "${path}" is not a string. Falling back to default value "${fallback}".`,
                );
                return fallback;
            }
            // Translation payloads are string-keyed/string-valued
            return value ?? fallback;
        },
        [translations],
    );

    return { t, isLoadingTranslations, error };
};

export default useDynolang;
